class WallpaperRollsCalculator extends HTMLElement {
    static WIDTH_FEET_DEVIDE = 12;
    static WIDTH_FEET_COEFFICIENT = 3.281;
    static INCH_TO_METER_COEFFICIENT = 0.0254;

    constructor() {
        super();
        this.resultTab = this.querySelector(".result-content") || null;
        this.addToCart = this.querySelector("#button-cart-calc") || null;
        this.resultWrapper = this.querySelector(".result") || null;
        this.wallpaperDescription = this.querySelector(".wallpaper-description") || null;
        this.wallpaperTitle = this.querySelector(".wallpaper-title") || null;
        this.productVariantImage = this.querySelector(".variant-image") || null
        this.form = this.querySelector(".calculation-form") || null;
        this.calculateButton = this.querySelector(".calculate") || null;
        this.reCalculateButton = this.querySelector(".recalculate") || null;
        this.switcher = this.querySelector(".wallpaper-switcher") || null;
        this.heightInputM = this.querySelector(".height-m .height") || null;
        this.heightInputIn = this.querySelector(".height-in .height") || null;
        this.heightInputFt = this.querySelector(".height-ft .height") || null;
        this.addMoreBtn = this.querySelector(".add-more") || null;
        this.inputList = this.querySelector(".dimension-list") || null;
        this.bespokeMessage = this.querySelector(".bespoke-message") || null;
        this.bespokeMessageIn = this.querySelector(".bespoke-message-in") || null;
        this.imperialSelector = this.querySelector(".imperial") || null;
        this.emptyMessage = this.querySelector(".error-empty") || null;
        this.enterMessage = this.querySelector(".error-enter") || null;
        this.enterMessageIn = this.querySelector(".error-enter-in") || null;
        this.heightOptions = JSON.parse(
            this.form.getAttribute("data-heights")?.replace(/--inches--/g, '\\"').replace(/--feet--/g, "'").replace(/ and /g, '')
        ) || null;
        this.widthOption = this.form.getAttribute("data-width") || null;
        this.sheetsOptions = this.form.getAttribute("data-sheets") || null;
        this.currency = this.form.getAttribute("data-currency") || null;

        this.maxValues = {
            height: 100,
            walls: 100,
            standart: {
                min: 4,
                max: 10
            }
        };

        this.units = {
            unitM: 'm',
            unitI: "inch",
            unitFt: "feet"
        };

        this.additionalFields = [{ index: 1, valueM: '', valueFt: '', valueIn: '' }];
        this.addMoreBtn.addEventListener("click", () => this.addInput());
        this.inputList.addEventListener("click", (event) => {
            if (event.target.classList.contains("remove-wall")) {
                this.removeInput(event.target);
            }
        });
        this.heightInputFt.addEventListener("input", (event) => {
            this.validateInputRange(event.target);
            this.checkInIfFeetIsDecimal(event.target);
            this.resetValidateMessages();
        });
        this.heightInputIn.addEventListener("input", (event) => {
            this.validateInputRange(event.target);
            this.checkDecimalsFeet(event.target);
            this.resetValidateMessages();
        });
        this.heightInputM.addEventListener("input", (event) => {
            this.validateInputRange(event.target);
            this.resetValidateMessages();
        });
        this.calculateButton.addEventListener("click", () => {
            const valid = this.validateInput();
            if (valid) { 
                const result = this.calculate();
                this.form.classList.toggle("hidden");
                this.resultRender(result);
            }
        }); 
        this.inputList.addEventListener("input", (event) => {
            if (event.target.classList.contains("input-text")) {
                this.validateInputRange(event.target);
                if (event.target.dataset.unit === "feet") this.checkInIfFeetIsDecimal(event.target);
                if (event.target.dataset.unit === "inches") this.checkDecimalsFeet(event.target);
                this.resetValidateMessages();
            }
        });
        this.reCalculateButton.addEventListener("click", () => {
            this.resultTab.classList.toggle("hidden");
            this.form.classList.toggle("hidden");
            this.resultWrapper.innerHTML = "";
            this.initAdditionalFields();
            this.wallpaperTitle.textContent = "What size is your room?";
            this.wallpaperDescription.textContent = "Use this handy calculator to work out how many litres of paint you will need. For support in placing this order, please contact our Customer Care team.";
            this.switcher.classList.remove("hidden");
        })
    }

    connectedCallback() {
        this.initAdditionalFields();
        if (this.heightOptions[0].value.includes('"')) {
            this.heightOptions.forEach(option => {
                option.value = this.convertHeightToMetres(option.value)
            });
        } else {
            this.heightOptions.forEach(option => {
                option.value = option.value.replace(' metres', '');
            });
        }
    }

    convertHeightToMetres(height) {
        const [feet, inches] = height.split("'");
        const totalInches = parseInt(feet, 10) * 12 + parseInt(inches, 10);
        return (totalInches * WallpaperRollsCalculator.INCH_TO_METER_COEFFICIENT).toFixed(1).replace(/\.?0+$/, '');
      }

    initAdditionalFields() {
        this.inputList.innerHTML = this.inputRender();
    }

    validateInputRange(input) {
        const min = parseInt(input.min);
        const max = parseInt(input.max);
        let value = parseInt(input.value);

        if (value < min) {
            input.value = min;
        } else if (value > max) {
            input.value = max;
        }
    }

    resultRender(result) {
        this.resultTab.classList.toggle("hidden");
        if (!this.switcher.classList.contains("hidden")) {
            this.switcher.classList.add('hidden');
        }

        this.wallpaperTitle.textContent = "You will need...";
        this.wallpaperDescription.textContent = `Based on your room size you will require an estimated ${result.rollsCount} rolls of ${this.renderVariantTitle(result)}`;
        this.resultWrapper.innerHTML = this.resultTemplate(result);
        this.addToCart.parentElement.classList.add("w-full");
        this.addToCart.textContent = "Add to bag - " + this.addToCart.dataset.currencySymbol + result.price.toFixed(2);
        this.addToCart.dataset.productId = result.variantId;
    }

    inputRender() {
        return this.additionalFields.map((elem, idx) => {
            const lastElement = this.additionalFields[this.additionalFields.length - 1];
            const isLast = lastElement.index === elem.index && idx !== 0;
            return this.inputTemplate({
                index: elem.index,
                valueM: elem.valueM, 
                valueFt: elem.valueFt, 
                valueIn: elem.valueIn,
                isLast,
            });
        }).join('');
    }

    inputTemplate({ index, isLast, valueM, valueFt, valueIn }) {
        return `<li>
                <div :class="{'hidden': imperial, 'block': !imperial}">
                    <div class="flex flex-row justify-between">
                        <span class="block mb-2 text-2xl font-alt">Wall ${index}</span>
                        ${isLast ? '<a href="#" @click.prevent class="remove-wall text-sm underline">Remove</a>' : ''}
                    </div>
                    <div class="input-holder relative">
                        <input min="1" type="number" class="input-text width width-m" value="${valueM}" placeholder="Width (in meters)">
                    </div>
                </div>
                <div :class="{'block': imperial, 'hidden': !imperial}" class="data-imperial-block">
                    <div class="flex flex-row justify-between">
                        <span class="block mb-2 text-2xl font-alt">Wall ${index}</span>
                        ${isLast ? '<a href="#" @click.prevent class="remove-wall text-sm underline">Remove</a>' : ''}
                    </div>
                    <div class="input-wrapper">
                        <div class="input-holder mb-2.5 relative">
                            <input min="1" type="number" class="input-text width widthfeet" value="${valueFt}" placeholder="Width (in feet)" data-unit="feet">
                        </div>
                        <div class="input-holder relative${isLast ? ' mb-5' : "" }">
                            <input min="1" type="number" class="input-text width widthinch" value="${valueIn}" placeholder="Width (in inches)" data-unit="inches">
                        </div>
                    </div>
                </div>
             </li>`;
    }

    addInput() {
        this.saveInputValues();
        const lastElement = this.additionalFields[this.additionalFields.length - 1];
        const newIndex = lastElement.index + 1;
        this.additionalFields.push({ index: newIndex, valueM: '', valueFt:'', valueIn: '' });
        if (this.additionalFields.length === 10) {
            this.addMoreBtn.classList.add("hidden");
        }
        this.initAdditionalFields();
    }

    removeInput(target) {
        this.saveInputValues();
        const indexToRemove = Array.from(this.inputList.querySelectorAll('li')).indexOf(target.closest('li'));
        if (indexToRemove > -1) {
            this.additionalFields.splice(indexToRemove, 1);
        }
        if (this.additionalFields.length < 4 && this.addMoreBtn.classList.contains("hidden")) {
            this.addMoreBtn.classList.remove("hidden");
        }
        this.initAdditionalFields();
    }

    saveInputValues() {
        const inputsM = this.inputList.querySelectorAll(".width-m");
        const inputsIn = this.inputList.querySelectorAll(".widthinch");
        const inputsFt = this.inputList.querySelectorAll(".widthfeet");
        inputsM.forEach((input, idx) => {
            this.additionalFields[idx].valueM = input.value;
        });
        inputsFt.forEach((input, idx) => {
            this.additionalFields[idx].valueFt = input.value;
        });
        inputsIn.forEach((input, idx) => {
            this.additionalFields[idx].valueIn = input.value;
        });
    }

    getInputValues() {
        this.saveInputValues();
        let totalWallLength = 0;
    
        console.log("getInputValues additionalFields:", this.additionalFields);
    
        this.additionalFields.forEach((field, index) => {
            const valueIn = parseFloat(field.valueIn);
            const valueFt = parseFloat(field.valueFt);
            const valueM = parseFloat(field.valueM);
            let wallLength;
    
            if (this.switcher.classList.contains("imperial")) {
                wallLength = this.formulaSwitcher(valueFt, valueIn, null);
            } else {
                wallLength = this.formulaSwitcher(null, null, valueM);
            }
            
            console.log(`Wall ${index + 1} calculation:`, {valueIn, valueFt, valueM, calculatedWallLength: wallLength});
    
            if (!isNaN(wallLength)) {
                totalWallLength += wallLength;
            }
        });
    
        console.log("getInputValues total wall length:", totalWallLength);
        return totalWallLength;
    }

    getHeightOptionRequired(wallHeight) {
        if (this.heightOptions) {
            let heightOptionRequired = {};
            for (let i = 0; i < this.heightOptions.length; i += 1) {
                if (wallHeight <= this.heightOptions[i].value) {
                    if (heightOptionRequired.value) {
                        const heightAsNumber = Number(heightOptionRequired.value);
                        if (heightAsNumber - wallHeight > Number(this.heightOptions[i].value) - wallHeight) {
                            heightOptionRequired = this.heightOptions[i];
                        }
                    } else {
                        heightOptionRequired = this.heightOptions[i];
                    }
                }
            }
            if (JSON.stringify(heightOptionRequired).length > 2) return heightOptionRequired;
        }
    }

    resetValidateMessages() {
        this.bespokeMessage.classList.add("hidden");
        this.bespokeMessageIn.classList.add("hidden");
        this.emptyMessage.classList.add("hidden");
        this.enterMessage.classList.add("hidden");
        this.enterMessageIn.classList.add("hidden");
    }

    checkDecimalsFeet(inTarget) {
        const numIn = Number(inTarget.value);
        if (isNaN(numIn) || !numIn) return;
        const feetInputRef = inTarget.closest(".data-imperial-block")?.querySelector("[data-unit='feet']")
        if (feetInputRef) {
            const numFt = Number(feetInputRef.value);
            if (isNaN(numFt) || !numFt) return;
            if (!Number.isInteger(numFt)) {
                feetInputRef.value = Math.trunc(numFt);
            }
        }
    }

    checkInIfFeetIsDecimal(feetTarget) {
        const numFt = Number(feetTarget.value);
        if (isNaN(numFt)) return;
        const inInputRef = feetTarget.closest(".data-imperial-block")?.querySelector("[data-unit='inches']")
        if (!Number.isInteger(numFt) && inInputRef.value) {
            inInputRef.value = "";
        };
    }

    validateInput() {
        this.resetValidateMessages(); 
        
        const walls = this.getInputValues();
        const feetValue = Math.ceil(this.heightInputFt.value);
        const inValue = Math.ceil(this.heightInputIn.value);
        const mValue = Math.ceil(this.heightInputM.value);
        let height = this.formulaSwitcher(feetValue, inValue, mValue);
        const heightOptionRequired = this.getHeightOptionRequired(height);

        let valid = true;

        if (!heightOptionRequired) {
            if (this.switcher.classList.contains("imperial")) {
                this.bespokeMessageIn.classList.remove("hidden");
            } else {
                this.bespokeMessage.classList.remove("hidden");
            }    
            valid = false;
        }  

        if (!height || !walls) {
            this.emptyMessage.classList.remove("hidden");
            valid = false;
        }

        if (height > this.maxValues.height && walls > this.maxValues.walls) {
            this.enterMessage.classList.remove("hidden");
            valid = false;
        }


        if (this.switcher.classList.contains("imperial") && height > this.maxValues.height && walls > this.maxValues.walls) {
            this.enterMessageIn.classList.remove("hidden");
            valid = false;
        }

        return valid;
    }

    calculate() {
        const feetValue = Math.ceil(this.heightInputFt.value);
        const inValue = Math.ceil(this.heightInputIn.value);
        const mValue = Math.ceil(this.heightInputM.value);
        const wallHeight = this.formulaSwitcher(feetValue, inValue, mValue);

        let wallLength = this.getInputValues();

        console.log("Calculate method - Initial values:", {
            feetValue,
            inValue,
            mValue,
            wallHeight,
            wallLength
        });

        const heightOptionRequired = this.getHeightOptionRequired(wallHeight);
        console.log("Selected height option:", heightOptionRequired);
    
        
        const variantRef = document.querySelector(`option[data-variant-title^="${heightOptionRequired.name.slice(0, 2)}"]`);
        console.log("variantRef: ", variantRef);
        const variantPrice = Number(variantRef.dataset.sizePrice) / 100; 


        console.log("Initial wallHeight:", wallHeight, "wallLength:", wallLength);
        console.log("heightOptionRequired:", heightOptionRequired);
        console.log("this.maxValues.standart:", this.maxValues.standart);
        console.log("this.widthOption:", this.widthOption);
        console.log("this.sheetsOptions:", this.sheetsOptions);
    
        //This is the calculation for a standard "roll" of wallpaper
        if (heightOptionRequired.value > this.maxValues.standart.min && heightOptionRequired.value <= this.maxValues.standart.max) {
            const no_of_cuts_needed = Math.ceil((wallLength / this.widthOption));
            const length_of_each_cut = Math.ceil(wallHeight / this.widthOption) * this.widthOption;
            const total_length_required = no_of_cuts_needed * length_of_each_cut;

            wallLength = Math.ceil(total_length_required / 10.05);
            console.log("Standard roll calculation:", {no_of_cuts_needed, length_of_each_cut, total_length_required, wallLength});

        }
        //This is the calculation for the pre-cut "sheets" of wallpaper
        else if (heightOptionRequired.value <= this.maxValues.standart.min) {
            const initialWallLength = wallLength;
            wallLength = Math.ceil((wallLength / this.widthOption));
            const intermediateWallLength = wallLength;
            wallLength = Math.ceil(wallLength / this.sheetsOptions);
            console.log("Pre-cut sheets calculation:", {initialWallLength, intermediateWallLength, finalWallLength: wallLength});
        }
        else {
            console.log("No calculation performed. Check conditions.");
        }
        console.log("Final wallLength: ", wallLength);

        console.log("wallLength: ", wallLength);

        this.addToCart.setAttribute(":data-product-qty", wallLength);

        return {
            rollsCount: wallLength,
            price: wallLength * variantPrice,
            variant: heightOptionRequired.name,
            variantInMeters: heightOptionRequired.value,
            variantId: Number(variantRef.value)
        }
    }

    formulaSwitcher(feetValue, inValue, mValue) {
        console.log("formulaSwitcher input:", {feetValue, inValue, mValue});
        if (this.switcher.classList.contains("imperial")) {
            let feet = isNaN(feetValue) ? 0 : feetValue;
            let inch = isNaN(inValue) ? 0 : inValue;
            let totalInches = feet * 12 + inch;
            let meters = totalInches * WallpaperRollsCalculator.INCH_TO_METER_COEFFICIENT;
            console.log("formulaSwitcher imperial calculation:", {feet, inch, totalInches, meters});
            return meters;
        } else {
            console.log("formulaSwitcher metric result:", Number(mValue));
            return Number(mValue);
        }	
    }

    renderVariantTitle(data, isMainInfo) {
        let variantTitle = data.variant;
        if (variantTitle.toLowerCase().includes('full')) {
            variantTitle = 'Full height'
        } else {
            variantTitle = 'Half height'
        }
        let size = data.variant.split(' - ')[0]
        if (this.switcher.classList.contains('metric')) {
            size = data.variantInMeters + 'M';
        }
        if (isMainInfo) return `${variantTitle} - ${size}`
        return `${variantTitle.toLowerCase()} wallpaper (${size})`
    }

    resultTemplate(newData) {
        let prodVarImgHTML = this.productVariantImage?.outerHTML;
        if (prodVarImgHTML) {
           prodVarImgHTML = prodVarImgHTML.replace(/hidden/g, '');
        }

        return `<div class="content w-full">
                    <div class="flex justify-between w-full items-center mb-7">
                    ${prodVarImgHTML}
                        <div class="flex justify-between w-full items-center">
                            <p><span class="text-3xl font-alt"><strong>${newData.rollsCount}x<br><br>Roll(s)</strong></span></p>
                            <p>${this.renderVariantTitle(newData, true)}</p>
                        </div>
                    </div>
                    <div class="w-full h-px bg-[#ABAEB3] rounded-sm mb-7"></div>
                    <div class="flex justify-between w-full items-center mb-7">
                        <p><span>Quantity</span></p>
                        <p>${newData.rollsCount}x Roll(s)</p>
                    </div>
                    <div class="flex justify-between w-full items-center mb-7">
                        <p><span>Total</span></p>
                        <p>${this.addToCart.dataset.currencySymbol}${newData.price.toFixed(2)}</p>
                    </div>
                </div>`
    }
}

customElements.define("wallpapers-calculator", WallpaperRollsCalculator);
